/* DeFi Nerve Center v4 — living protocol nervous system (canonical
   architecture): LAYER A master neural environment (permanent art, nucleus
   anchored) + LAYER B count-specific transparent topology plate (painted
   nerves, no identities, no data) + LAYER C dynamic live black-backed
   protocol satellites (DOM: logo, 2px animated stroke, TVL, 24H, entity
   nav) + LAYER D mask-based electrical firing (nerveRenderer.ts).
   Protocol count is data-driven (desktop 3-8, tablet 3-6, mobile 2-4);
   membership changes run a STABLE→EXITING→SWAP→ENTERING state machine with
   plate crossfade + stable slot migration. No entity-name layout forks; no
   fixed-count assumptions. Geometry comes from generated metadata
   (nerveTopology.ts) — no magic numbers here. */
import { Component, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Info } from "lucide-react";
import { Pct } from "@/components/layernav/primitives";
import { fmtBig, sectorTrack } from "./SectorHero";
import { ProtocolLogo } from "./ProtocolLogo";
import api, { timeAgo } from "@/lib/api";
import { entityNavRoute, entityTrack, resolveEntity, useEntityRegistry } from "@/lib/entities";
import { useEntitySync } from "./entitySync";
import {
  Circuit, FxScene, FxSlot, buildStrands, cubic, drawMotionFrame, fireSoon, fireState, hashStr, newSurge, syncCircuits,
} from "./nerveRenderer";
import { createTrafficEngine, fixtureEvents } from "./nerveTraffic";
import { NERVE_ASSET_BASE, NERVE_ENV, NERVE_TOPOLOGIES, NerveTopologyCfg } from "./nerveTopology";

const DISCLAIMER = "Directional currents reflect live classified spot trades for associated protocol tokens. Visual intensity is normalized and aggregated for readability. Signals do not represent direct fund flows into or out of protocol TVL.";

/* visible-count limits per breakpoint (spec §15-16, §60) */
const LIMITS: Record<string, [number, number]> = { desktop: [3, 8], tablet: [3, 6], mobile: [2, 4] };
const medScale = (family: string, count: number) =>
  family === "mobile" ? 0.92 : count >= 6 ? 1.14 : count === 5 ? 1.02 : 0.9;

/* Bounded activity normalization (founder-locked): |24H Δ| 0→10% via sqrt,
   CAPPED — outliers never scale proportionally. Sign handled separately. */
const activityIntensity = (d?: number | null) =>
  d == null ? 0.25 : Math.min(1, Math.sqrt(Math.min(Math.abs(d), 10) / 10));
const easeIO = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

/* stable slot migration (§33, §37): retained protocols take the nearest new
   slot; entrants fill remaining slots in rank order. Deterministic. */
function assignSlots(prevPos: Map<string, number[]>, slugs: string[], slots: any[]) {
  const free = slots.map((_, i) => i);
  const out = new Map<string, number>();
  slugs.filter((s) => prevPos.has(s)).forEach((s) => {
    const [px, py] = prevPos.get(s)!;
    let bi = 0, bd = Infinity;
    free.forEach((fi, idx) => {
      const d = Math.hypot(slots[fi].x - px, slots[fi].y - py);
      if (d < bd) { bd = d; bi = idx; }
    });
    out.set(s, free[bi]);
    free.splice(bi, 1);
  });
  slugs.filter((s) => !prevPos.has(s)).forEach((s) => out.set(s, free.shift()!));
  return out;
}

const plateSrc = (c: NerveTopologyCfg) => `${NERVE_ASSET_BASE}/${c.plate}`;

const PlateImg = ({ src, on, id }: { src: string; on: boolean; id: string }) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(t);
  }, []);
  return (
    <img src={src} alt="" aria-hidden draggable={false} data-testid={`nerve-plate-${id}`}
      className="nerve-plate absolute inset-0 w-full h-full select-none pointer-events-none z-[1]"
      style={{ opacity: on && ready ? 1 : 0 }} />
  );
};

/* Crisp real HTML metrics inside the nucleus chamber (§91) */
const CoreText = ({ env, pulse, stageW, family }: any) => (
  <div data-testid="nerve-core" className="absolute text-center pointer-events-none z-[6]"
    style={{
      left: `${env.nucleus.x * 100}%`, top: `${env.nucleus.y * 100}%`,
      width: `${env.nucleus.ri * 2 * 100}%`, transform: "translate(-50%,-50%)",
    }}>
    <span className="block font-mono font-bold uppercase tracking-[0.32em] text-[#B9A6FF]"
      style={{ fontSize: Math.max(8, stageW * 0.011) }}>DeFi TVL</span>
    <span className="block font-mono font-extrabold text-white mt-0.5"
      style={{
        fontSize: Math.max(18, stageW * (family === "mobile" ? 0.052 : 0.033)),
        textShadow: "0 0 26px rgba(0,229,255,.35), 0 0 60px rgba(124,58,237,.3)",
      }}>{fmtBig(pulse?.tvl_usd)}</span>
    {pulse?.tvl_change_24h_pct != null && (
      <span className={`block font-mono font-bold mt-0.5 ${pulse.tvl_change_24h_pct >= 0 ? "text-[#22C55E]" : "text-[#EF4444]"}`}
        style={{ fontSize: Math.max(9, stageW * 0.012) }}>
        {`${pulse.tvl_change_24h_pct >= 0 ? "▲ +" : "▼ "}${Math.abs(pulse.tvl_change_24h_pct).toFixed(2)}% 24H`}
      </span>
    )}
  </div>
);

const NerveVisual = ({ protocols, pulse, isStale }: { protocols: any[]; pulse: any; isStale: boolean }) => {
  const stageRef = useRef<HTMLDivElement>(null);
  const fxRef = useRef<HTMLCanvasElement>(null);
  const nodeEls = useRef<Record<string, HTMLButtonElement | null>>({});
  const posesRef = useRef<Map<string, any>>(new Map());
  const machineRef = useRef<any>({ state: "INIT", t0: 0, key: "", next: null, pending: null });
  const cfgRef = useRef<NerveTopologyCfg | null>(null);
  const sceneRef = useRef<FxScene | null>(null);
  const circuitsRef = useRef<Map<string, Circuit>>(new Map());
  const imgsRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const tmpsRef = useRef<Map<string, HTMLCanvasElement>>(new Map());
  const dirtyRef = useRef(true);
  const activeRef = useRef<string | null>(null);
  const animOkRef = useRef(true);
  const surgeRef = useRef(newSurge());
  const surgeStrengthRef = useRef(0);
  const surgeForceUntilRef = useRef(0);
  const trafficRef = useRef(createTrafficEngine());
  const lenFRef = useRef<{ cfg: any; bySlot: Map<string, number> | null }>({ cfg: null, bySlot: null });
  const visibleSlugsRef = useRef<Set<string>>(new Set());
  const telemetryElRef = useRef<HTMLPreElement | null>(null);
  const telemetryAtRef = useRef(0);
  const trafficDebug = useRef(new URLSearchParams(window.location.search).get("nerve_traffic_debug") === "1").current;
  /* QA fixtures are strictly dev/test-only: ignored in production builds,
     URL-param driven (never persisted), zero analytics contamination */
  const fixtureKeyRef = useRef(process.env.NODE_ENV === "production" ? null
    : new URLSearchParams(window.location.search).get("trafficFixture")
      || new URLSearchParams(window.location.search).get("nerve_fixture"));
  const debugRef = useRef(new URLSearchParams(window.location.search).get("nerve_debug") === "1");
  const reconcileRef = useRef<(rows: any[]) => void>(() => {});

  const navigate = useNavigate();
  const { data: reg } = useEntityRegistry();
  const { activeId, setActiveId } = useEntitySync();
  const [stageW, setStageW] = useState(0);
  const [canvasFailed, setCanvasFailed] = useState(false);
  const [plates, setPlates] = useState<{ key: string; src: string; on: boolean }[]>([]);
  const [renderList, setRenderList] = useState<any[]>([]);
  const [motion, setMotion] = useState(() => !window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const [offscreen, setOffscreen] = useState(false);
  const [docHidden, setDocHidden] = useState(() => document.hidden);
  const [nOverride, setNOverride] = useState<number | null>(() => {
    const v = parseInt(new URLSearchParams(window.location.search).get("nerve_n") || "", 10);
    return Number.isFinite(v) ? v : null;
  });

  const tier = stageW >= 800 ? "desktop" : stageW >= 520 ? "tablet"
    : stageW > 0 ? "mobile" : window.innerWidth < 640 ? "mobile" : "desktop";
  const family = tier === "mobile" ? "mobile" : "desktop";
  const familyRef = useRef(family);
  familyRef.current = family;
  const staleRef = useRef(isStale);
  staleRef.current = isStale;
  const env = NERVE_ENV[family];

  const eligible = useMemo(
    () => (protocols || []).filter((p) => p.slug && p.protocol && p.tvl_usd != null),
    [protocols]);
  const [minN, maxN] = LIMITS[tier];
  const fams: any = NERVE_TOPOLOGIES[family] || {};
  let count = Math.min(eligible.length, Math.max(minN, Math.min(maxN, nOverride ?? maxN)));
  while (count >= minN && !fams[count]) count--;
  const visible = useMemo(() => eligible.slice(0, count), [eligible, count]);
  const lowCount = eligible.length < minN || count < minN;

  const activeSlug = useMemo(() => {
    const m = visible.find((p) => `protocol:${p.slug}` === activeId);
    return m ? m.slug : null;
  }, [activeId, visible]);

  const getImg = (src: string) => {
    let im = imgsRef.current.get(src);
    if (!im) {
      im = new Image();
      im.onload = () => { dirtyRef.current = true; };
      im.src = `${NERVE_ASSET_BASE}/${src}`;
      imgsRef.current.set(src, im);
    }
    return im;
  };

  /* reconcile live data -> topology + poses (state machine entry, §104) */
  reconcileRef.current = (rows: any[]) => {
    const fam = familyRef.current;
    const cfgN: NerveTopologyCfg | undefined = (NERVE_TOPOLOGIES as any)[fam]?.[rows.length];
    if (!cfgN || !rows.length) return;
    const now = performance.now();
    const m = machineRef.current, poses = posesRef.current;
    if (m.state !== "STABLE" && m.state !== "INIT") { m.pending = rows; return; }
    const cur = Array.from(poses.entries()).filter(([, p]) => !p.exiting).map(([s]) => s);
    const slugs = rows.map((r) => r.slug);
    const same = cur.length === slugs.length && slugs.every((s) => cur.includes(s));
    if (same && cfgN.id === m.key) {
      /* §39: value refresh only — no topology change, keep slots */
      rows.forEach((r) => {
        const p = poses.get(r.slug);
        if (p) { p.row = r; p.I = activityIntensity(r.tvl_change_24h_pct); }
      });
      dirtyRef.current = true;
      setRenderList(rows);
      return;
    }
    if (m.state === "INIT") {
      rows.forEach((r, i) => {
        const sl = cfgN.slots[i];
        poses.set(r.slug, {
          row: r, I: activityIntensity(r.tvl_change_24h_pct), slot: i, side: sl.labelSide, rF: sl.r,
          x: sl.x, y: sl.y, a: 0, fx: sl.x, fy: sl.y, fa: 0, tx: sl.x, ty: sl.y, ta: 1,
          t0: now + i * 90, dur: 640, exiting: false, justEntered: true,
        });
      });
      m.key = cfgN.id;
      cfgRef.current = cfgN;
      m.state = "ENTERING";
      m.t0 = now + 460;
      setPlates([{ key: cfgN.id, src: plateSrc(cfgN), on: true }]);
      setRenderList(rows);
      dirtyRef.current = true;
      return;
    }
    /* full topology transition: EXITING first (§35) */
    const pre = new Image();
    pre.src = plateSrc(cfgN);
    m.next = { rows, key: cfgN.id, cfg: cfgN };
    m.state = "EXITING";
    m.t0 = now;
    if (sceneRef.current) sceneRef.current.paused = true;
    const stay = new Set(slugs);
    poses.forEach((p, s) => {
      if (!stay.has(s) && !p.exiting) {
        p.exiting = true;
        p.fx = p.x; p.fy = p.y; p.fa = p.a;
        p.tx = p.x; p.ty = p.y; p.ta = 0;
        p.t0 = now; p.dur = 400;
      }
    });
    dirtyRef.current = true;
  };

  const rowsKey = visible.map((p) => `${p.slug}:${p.tvl_change_24h_pct}`).join("|") + "·" + family;
  useEffect(() => {
    visibleSlugsRef.current = new Set(visible.map((p) => p.slug));
    if (!lowCount) reconcileRef.current(visible);
  }, [rowsKey, lowCount]); // eslint-disable-line react-hooks/exhaustive-deps

  /* live traffic ingestion — dev event now; real classified feed later
     plugs in through the same contract (nerveTraffic.ts) */
  useEffect(() => {
    const onTraffic = (e: any) => {
      trafficRef.current.ingest(e?.detail?.events || [], visibleSlugsRef.current, performance.now());
    };
    window.addEventListener("nerve:traffic", onTraffic);
    if (process.env.NODE_ENV !== "production" && (trafficDebug || fixtureKeyRef.current)) {
      (window as any).__nerveTraffic = trafficRef.current; // dev QA read handle
      const slow = parseFloat(new URLSearchParams(window.location.search).get("nerve_traffic_slow") || "");
      if (Number.isFinite(slow) && slow > 0) trafficRef.current.slowF = slow; // dev slow-mo
    }
    return () => window.removeEventListener("nerve:traffic", onTraffic);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* QA fixtures A-H + BURST (?trafficFixture=) — dev-only synthetic feeds */
  useEffect(() => {
    const key = fixtureKeyRef.current;
    if (!key) return;
    console.info(`[NerveCenter QA] traffic fixture "${key.toUpperCase()}" ACTIVE — dev/test only, never runs in production`);
    const id = setInterval(() => {
      trafficRef.current.ingest(
        fixtureEvents(key, Array.from(visibleSlugsRef.current), performance.now()),
        visibleSlugsRef.current, performance.now());
    }, 2000);
    /* fixture H — membership change while traffic is active */
    const hId = key.toUpperCase() === "H"
      ? setInterval(() => setNOverride((v) => (v === 5 ? 6 : 5)), 9000) : null;
    return () => { clearInterval(id); if (hId) clearInterval(hId); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* LIVE classified trade feed (Kraken taker-side spot trades for the
     displayed protocols) — polls only while visible & animating; fixtures
     replace the live feed during dev QA. Provider hiccups are tolerated:
     the neutral activity field keeps running (truth hierarchy L1→L3). */
  useEffect(() => {
    if (fixtureKeyRef.current) return;
    let cursor = 0, active = true, inflight = false;
    const poll = async () => {
      if (!active || inflight || !animOkRef.current || document.hidden) return;
      inflight = true;
      try {
        const r = await api.get(`/defi/traffic?since_seq=${cursor}`);
        if (!active) return;
        cursor = r.data?.last_seq ?? cursor;
        const evs = r.data?.events || [];
        if (evs.length) trafficRef.current.ingest(evs, visibleSlugsRef.current, performance.now());
      } catch { /* keep neutral field */ } finally { inflight = false; }
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => { active = false; clearInterval(id); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* preload adjacent topologies (§41) */
  useEffect(() => {
    [count - 1, count + 1].forEach((n) => {
      const c = (NERVE_TOPOLOGIES as any)[family]?.[n];
      if (c) { const im = new Image(); im.src = plateSrc(c); }
    });
  }, [count, family]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const f = (e: any) => setMotion(!e.matches);
    mq.addEventListener("change", f);
    const onVis = () => setDocHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => { mq.removeEventListener("change", f); document.removeEventListener("visibilitychange", onVis); };
  }, []);
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver((es) => { setStageW(Math.round(es[0].contentRect.width)); dirtyRef.current = true; });
    ro.observe(el);
    const io = new IntersectionObserver((es) => setOffscreen(es[0].intersectionRatio === 0), { threshold: [0] });
    io.observe(el);
    return () => { ro.disconnect(); io.disconnect(); };
  }, [lowCount, canvasFailed]);
  useEffect(() => { animOkRef.current = motion && !offscreen && !docHidden; }, [motion, offscreen, docHidden]);

  /* hover priority: dim others + one controlled loop for the target (§57) */
  useEffect(() => {
    activeRef.current = activeSlug;
    dirtyRef.current = true;
    if (activeSlug) fireSoon(circuitsRef.current, activeSlug, performance.now());
  }, [activeSlug]);

  /* Nucleus Burst gate — REAL market movement only (§52): sector TVL 24H
     move ≥5% or capped avg protocol intensity ≥0.75. 0 disables surges. */
  useEffect(() => {
    const d = Math.abs(pulse?.tvl_change_24h_pct ?? 0);
    const avg = visible.reduce((s, p) => s + activityIntensity(p.tvl_change_24h_pct), 0) / (visible.length || 1);
    surgeStrengthRef.current = Math.max(d >= 5 ? Math.min(1, d / 8) : 0, avg >= 0.75 ? avg : 0);
    dirtyRef.current = true;
  }, [pulse, visible]);

  /* dev controls (§98-99): nerve:count {n}, nerve:fire {slot}, nerve:surge */
  useEffect(() => {
    const onCount = (e: any) => setNOverride(Number.isFinite(e?.detail?.n) ? e.detail.n : null);
    const onFire = (e: any) => {
      const si = e?.detail?.slot ?? 0;
      posesRef.current.forEach((p, slug) => {
        if (p.slot === si) fireSoon(circuitsRef.current, slug, performance.now());
      });
    };
    const onSurge = () => {
      surgeForceUntilRef.current = performance.now() + 15000;
      surgeRef.current.phase = 0;
      surgeRef.current.nextAt = performance.now() + 300;
      dirtyRef.current = true;
    };
    window.addEventListener("nerve:count", onCount);
    window.addEventListener("nerve:fire", onFire);
    window.addEventListener("nerve:surge", onSurge);
    return () => {
      window.removeEventListener("nerve:count", onCount);
      window.removeEventListener("nerve:fire", onFire);
      window.removeEventListener("nerve:surge", onSurge);
    };
  }, []);

  /* single animation runtime — poses, state machine, scene, electrical FX.
     No per-frame React state (§80). */
  useEffect(() => {
    const stage = stageRef.current, fx = fxRef.current;
    if (!stage || !fx) return;
    const ctx = fx.getContext("2d");
    if (!ctx) { setCanvasFailed(true); return; }
    let raf = 0, running = true, cleared = false;
    /* adaptive quality — sustained frame-time EMA drops to lite mode on
       low-power devices (fewer strands/sparks, capped DPR), recovers with
       hysteresis so it never oscillates */
    let q = 1, ema = 16.7, loCnt = 0, hiCnt = 0, prevT = 0;
    const strandCache = new Map<string, number[][][]>();

    const doSwap = (now: number) => {
      const m = machineRef.current, poses = posesRef.current;
      const { rows, key, cfg } = m.next;
      m.next = null;
      poses.forEach((p, s) => { if (p.exiting) { poses.delete(s); delete nodeEls.current[s]; } });
      const prevPos = new Map<string, number[]>(Array.from(poses.entries()).map(([s, p]) => [s, [p.x, p.y]]));
      const order = assignSlots(prevPos, rows.map((r: any) => r.slug), cfg.slots);
      rows.forEach((r: any) => {
        const si = order.get(r.slug)!;
        const sl = cfg.slots[si];
        const I = activityIntensity(r.tvl_change_24h_pct);
        const ex = poses.get(r.slug);
        if (ex) {
          Object.assign(ex, { row: r, I, slot: si, side: sl.labelSide, rF: sl.r,
            fx: ex.x, fy: ex.y, fa: ex.a, tx: sl.x, ty: sl.y, ta: 1, t0: now, dur: 700 });
        } else {
          poses.set(r.slug, { row: r, I, slot: si, side: sl.labelSide, rF: sl.r,
            x: sl.x, y: sl.y, a: 0, fx: sl.x, fy: sl.y, fa: 0, tx: sl.x, ty: sl.y, ta: 0,
            t0: now, dur: 1, exiting: false, entering: true, justEntered: true });
        }
      });
      m.key = key;
      cfgRef.current = cfg;
      m.state = "SWAP";
      m.t0 = now;
      circuitsRef.current.clear();
      dirtyRef.current = true;
      setPlates((ps) => [...ps.filter((p) => p.on).map((p) => ({ ...p, on: false })), { key, src: plateSrc(cfg), on: true }]);
      setRenderList(rows);
    };

    const step = (now: number) => {
      if (!running) return;
      if (prevT) {
        const dt = now - prevT;
        if (dt < 250) {
          ema = ema * 0.95 + dt * 0.05;
          if (q === 1 && ema > 26) { if (++loCnt > 90) { q = 0.6; loCnt = 0; dirtyRef.current = true; } }
          else if (q < 1 && ema < 17) { if (++hiCnt > 240) { q = 1; hiCnt = 0; dirtyRef.current = true; } }
          else { loCnt = 0; hiCnt = 0; }
        }
      }
      prevT = now;
      const w = stage.clientWidth, h = stage.clientHeight;
      if (w > 4 && h > 4) {
        const d = Math.min(window.devicePixelRatio || 1, familyRef.current === "mobile" ? 1.75 : 2, q < 1 ? 1.5 : 8);
        if (fx.width !== Math.round(w * d) || fx.height !== Math.round(h * d)) {
          fx.width = Math.round(w * d);
          fx.height = Math.round(h * d);
          dirtyRef.current = true;
        }
        const poses = posesRef.current, m = machineRef.current;
        let moving = false;
        poses.forEach((p, slug) => {
          const k = Math.max(0, Math.min(1, (now - p.t0) / p.dur));
          const e = easeIO(k);
          p.x = p.fx + (p.tx - p.fx) * e;
          p.y = p.fy + (p.ty - p.fy) * e;
          p.a = p.fa + (p.ta - p.fa) * e;
          if (k < 1) moving = true;
          const el = nodeEls.current[slug];
          if (el) {
            el.style.left = `${p.x * 100}%`;
            el.style.top = `${p.y * 100}%`;
            el.style.opacity = String(p.a);
          }
        });
        if (moving) dirtyRef.current = true;

        if (m.state === "EXITING" && now - m.t0 >= 430) doSwap(now);
        else if (m.state === "SWAP" && now - m.t0 >= 730) {
          m.state = "ENTERING";
          m.t0 = now;
          let j = 0;
          poses.forEach((p) => {
            if (p.entering) { p.entering = false; p.fa = 0; p.ta = 1; p.fx = p.x; p.fy = p.y; p.t0 = now + j * 110; p.dur = 430; j++; }
          });
          dirtyRef.current = true;
        } else if (m.state === "ENTERING" && now - m.t0 >= 620) {
          m.state = "STABLE";
          if (sceneRef.current) sceneRef.current.paused = false;
          let j = 0;
          poses.forEach((p, slug) => {
            if (p.justEntered) {
              p.justEntered = false;
              const c = circuitsRef.current.get(slug);
              if (c) c.nextAt = now + 320 + j * 480;
              j++;
            }
          });
          setPlates((ps) => (ps.some((pl) => !pl.on) ? ps.filter((pl) => pl.on) : ps));
          if (m.pending) { const rows = m.pending; m.pending = null; reconcileRef.current(rows); }
        }

        if (dirtyRef.current) {
          const fam = familyRef.current;
          const envC = (NERVE_ENV as any)[fam];
          const cfgN = cfgRef.current;
          if (cfgN && envC) {
            const slots: FxSlot[] = [];
            let sumI = 0;
            poses.forEach((p, slug) => {
              if (p.exiting) return;
              const sl = cfgN.slots[p.slot];
              if (!sl) return;
              let tmp = tmpsRef.current.get(slug);
              if (!tmp) { tmp = document.createElement("canvas"); tmpsRef.current.set(slug, tmp); }
              const P = sl.guide.map((g: number[]) => [g[0] * w, g[1] * h]);
              const fwPx = Math.max(10, sl.fiber.width * w);
              const seed = hashStr(slug);
              slots.push({
                slug, I: p.I, alpha: p.a, seed,
                P, tEnd: sl.tEnd, cx: sl.x * w, cy: sl.y * h, r: sl.r * w,
                exitA: Math.atan2(sl.departure[1] * h - envC.nucleus.y * h, sl.departure[0] * w - envC.nucleus.x * w),
                fw: fwPx,
                bbox: [sl.fiber.bbox[0] * w, sl.fiber.bbox[1] * h, sl.fiber.bbox[2] * w, sl.fiber.bbox[3] * h],
                img: getImg(sl.fiber.src), tmp,
                strands: (() => {
                  const ck = `${slug}|${w}x${h}`;
                  let st = strandCache.get(ck);
                  if (!st) {
                    if (strandCache.size > 48) strandCache.clear();
                    st = buildStrands(P, fwPx, seed, fam === "mobile" ? 7 : 14);
                    strandCache.set(ck, st);
                  }
                  return st;
                })(),
              });
              sumI += p.I;
            });
            sceneRef.current = {
              w, h, u: w / (fam === "mobile" ? 430 : 1000),
              tier: fam === "mobile" ? "mobile" : "desktop",
              staleF: staleRef.current ? 1.6 : 1, avgI: slots.length ? sumI / slots.length : 0.4,
              surge: performance.now() < surgeForceUntilRef.current ? 1 : surgeStrengthRef.current,
              q,
              cx: envC.nucleus.x * w, cy: envC.nucleus.y * h,
              Ri: envC.nucleus.ri * w, rMid: envC.nucleus.rm * w, Ro: envC.nucleus.ro * w,
              paused: m.state !== "STABLE", slots,
            };
            syncCircuits(circuitsRef.current, sceneRef.current, now);
            dirtyRef.current = false;
          }
        }

        /* traffic engine: burst lifecycle side-effects + windowed flush */
        const eng = trafficRef.current;
        eng.bursts.forEach((b: any) => {
          const micro = b.tier === 0;
          if (!b._started && now >= b.t0) {
            b._started = true;
            if (b.dir === 1) eng.setFlag(b.slug, micro ? "hitm" : "hit", now, micro ? 520 : 500); // BUY departs: satellite ring pulses
          }
          if (!b._done && now >= b.t0 + b.dur) {
            b._done = true;
            if (b.dir === 1) {
              const p = poses.get(b.slug);
              const sl = p != null && cfgRef.current ? cfgRef.current.slots[p.slot] : null;
              /* reaction accumulator: micro arrivals contribute small bounded
                 power — several inside the merge window combine into ONE
                 richer nucleus response (§no-over-pulse) */
              eng.mergePulse(now, micro ? 0.15 + 0.12 * b.conc : 0.35 + 0.18 * b.tier + 0.2 * Math.max(0, b.conc - 0.35), sl ? sl.departure : null);
            } else eng.setFlag(b.slug, micro ? "sendm" : "send", now, micro ? 560 : 650); // SELL arrives: satellite stroke pulses
          }
        });
        /* §topology-change: new traffic bursts pause during transitions —
           no current may travel toward an orb being removed */
        if (m.state === "STABLE" || m.state === "INIT") {
          const slotOf = new Map<string, number>();
          poses.forEach((p, slug) => { if (!p.exiting) slotOf.set(slug, p.slot); });
          /* §length-aware speed: consistent apparent velocity — longer nerves
             get proportionally longer travel (computed once per topology) */
          if (lenFRef.current.cfg !== cfgRef.current && cfgRef.current) {
            const raw = new Map<string, number>();
            cfgRef.current.slots.forEach((sl: any, i: number) => {
              let L = 0; let prev: number[] | null = null;
              for (let k = 0; k <= 10; k++) {
                const p = cubic(sl.guide, 0.02 + (sl.tEnd - 0.02) * (k / 10));
                const pt = [p[0] * 1.55, p[1]];
                if (prev) L += Math.hypot(pt[0] - prev[0], pt[1] - prev[1]);
                prev = pt;
              }
              raw.set(String(i), L);
            });
            const lens = [...raw.values()].sort((a, b2) => a - b2);
            const med = lens[Math.floor(lens.length / 2)] || 1;
            lenFRef.current = { cfg: cfgRef.current, bySlot: new Map([...raw].map(([i, L]) => [i, Math.max(0.8, Math.min(1.35, L / med))])) };
          }
          const lenOf = new Map<string, number>();
          slotOf.forEach((slot, slug) => lenOf.set(slug, lenFRef.current.bySlot?.get(String(slot)) ?? 1));
          eng.flush(now, slotOf, familyRef.current === "mobile" ? 1 : 3, lenOf);
        }

        const sc = sceneRef.current;
        if (sc) {
          if (animOkRef.current) {
            drawMotionFrame(ctx, sc, circuitsRef.current, now, {
              dpr: d, activeSlug: activeRef.current, debug: debugRef.current, surge: surgeRef.current,
              traffic: { bursts: eng.bursts, pulse: eng.pulse, suppress: eng.suppress },
            });
            cleared = false;
          } else if (!cleared) {
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.clearRect(0, 0, fx.width, fx.height);
            cleared = true;
          }
          /* stroke sync always runs — in reduced motion, classified activity
             is still conveyed via static stroke intensity (§REDUCED MOTION) */
          poses.forEach((p, slug) => {
            const el = nodeEls.current[slug];
            if (!el) return;
            const v = fireState(circuitsRef.current.get(slug), now) || eng.fireFlag(slug, now);
            if (el.dataset.fire !== v) el.dataset.fire = v;
          });
        }
        /* dev QA telemetry (?nerve_traffic_debug=1) */
        if (telemetryElRef.current && now - telemetryAtRef.current > 500) {
          telemetryAtRef.current = now;
          const st = eng.stats;
          telemetryElRef.current.textContent =
            `TRAFFIC LEVEL ${eng.level}${eng.level === 3 ? " (activity only — no directional data)" : " (classified)"}\n` +
            `in=${st.in} buy=${st.buy} sell=${st.sell} unk=${st.unk} dropped=${st.dropped} ghosts=${st.ghosts}\n` +
            `bursts=${eng.bursts.length} suppress=[${Array.from(eng.suppress).join(",")}]\n` +
            "――――――――――――――――――\n" + eng.telemetry.slice(-12).join("\n");
        }
      }
      /* idle throttle: offscreen / hidden tab / reduced motion → ~4fps
         housekeeping loop instead of a full-rate rAF */
      if (!animOkRef.current) setTimeout(() => { if (running) raf = requestAnimationFrame(step); }, 250);
      else raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => { running = false; cancelAnimationFrame(raf); };
  }, [lowCount, canvasFailed]); // eslint-disable-line react-hooks/exhaustive-deps

  if (lowCount || canvasFailed) return <StaticFallback protocols={eligible} pulse={pulse} />;

  return (
    <div>
      <div ref={stageRef} className="relative rounded-xl border border-white/[0.06] overflow-hidden"
        style={{ aspectRatio: `${env.w}/${env.h}`, background: "#04040a" }} data-testid="nerve-center-visual">
        {/* LAYER A — master neural environment (permanent, nucleus anchored) */}
        <img src={`${NERVE_ASSET_BASE}/${env.src}`} alt="" aria-hidden draggable={false}
          data-testid="nerve-environment"
          className="absolute inset-0 w-full h-full select-none pointer-events-none" />
        {/* LAYER B — count-specific transparent topology plate (crossfades) */}
        {plates.map((pl) => <PlateImg key={pl.key} id={pl.key} src={pl.src} on={pl.on} />)}
        {/* LAYER D — electrical FX canvas (mask-lit painted fibers) */}
        <canvas ref={fxRef} className="absolute inset-0 w-full h-full z-[2]" aria-hidden data-testid="nerve-canvas-fx" />

        {/* LAYER C — dynamic live protocol satellites (black-backed, 2px
            animated stroke, real data, entity nav; identity never in art) */}
        {renderList.map((p: any) => {
          const pose = posesRef.current.get(p.slug);
          if (!pose) return null;
          const d24 = p.tvl_change_24h_pct;
          const isActive = activeSlug === p.slug;
          const dimmed = activeSlug != null && !isActive;
          const route = entityNavRoute(resolveEntity(reg, "protocol", p.slug));
          const m = Math.max(42, Math.round(pose.rF * 2 * (stageW || 900) * medScale(family, count)));
          const side = pose.side;
          const labelPos = side === "above" ? "bottom-full left-1/2 -translate-x-1/2 pb-1 items-center text-center"
            : side === "right" ? "left-full top-1/2 -translate-y-1/2 pl-1.5 items-start text-left"
            : side === "left" ? "right-full top-1/2 -translate-y-1/2 pr-1.5 items-end text-right"
            : "top-full left-1/2 -translate-x-1/2 pt-1 items-center text-center";
          return (
            <button key={p.slug} type="button"
              ref={(el) => { nodeEls.current[p.slug] = el; }}
              data-testid={`nerve-node-${p.slug}`} data-live={route ? "true" : "false"} data-fire=""
              className={`absolute grid place-items-center z-[3] ${route ? "cursor-pointer" : "cursor-default"} bg-transparent border-0 p-1.5 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[#00E5FF]/70 transition-[filter] duration-200 ${dimmed ? "brightness-[.6]" : ""}`}
              style={{ left: `${pose.x * 100}%`, top: `${pose.y * 100}%`, opacity: pose.a, transform: "translate(-50%,-50%)" }}
              onMouseEnter={() => setActiveId(`protocol:${p.slug}`)} onMouseLeave={() => setActiveId(null)}
              onFocus={() => setActiveId(`protocol:${p.slug}`)} onBlur={() => setActiveId(null)}
              onClick={() => {
                if (!route) return;
                entityTrack("entity_nav_click", `protocol:${p.slug}`, { surface: "nerve_center" });
                navigate(route);
              }}
              aria-label={`${p.protocol}: ${fmtBig(p.tvl_usd)} TVL${d24 != null ? `, ${d24 >= 0 ? "up" : "down"} ${Math.abs(d24).toFixed(2)} percent over 24 hours` : ""}${p.category ? `, ${p.category}` : ""}${route ? ". View entity page." : ""}`}>
              <span className="relative grid place-items-center" style={{ width: m + 16, height: m + 16, transition: "width .5s ease, height .5s ease" }}>
                {/* restrained halo (cyan = active/focus, never direction) */}
                <span aria-hidden className="absolute rounded-full transition-opacity duration-200" style={{
                  inset: -14, filter: "blur(13px)", opacity: isActive ? 1 : 0.55,
                  background: isActive
                    ? "radial-gradient(circle, rgba(0,229,255,.42), transparent 70%)"
                    : "radial-gradient(circle, rgba(124,58,237,.32), transparent 72%)" }} />
                {/* full black opaque backing (§29) */}
                <span aria-hidden className="nc4-plate absolute rounded-full" style={{ inset: 8 }} />
                {/* 3px aurora arc stroke — counter-rotating arc layers */}
                <span aria-hidden className="nc4-stroke absolute rounded-full" style={{ inset: 5 }} />
                <span aria-hidden className="nc4-stroke-b absolute rounded-full" style={{ inset: 5 }} />
                {/* electrical stroke glint — traveling hot-arc on fire events */}
                <span aria-hidden className="nc4-glint absolute rounded-full" style={{ inset: 5 }} />
                {/* light burst on arrival / departure — synced via data-fire */}
                <span aria-hidden className="nc4-burst absolute rounded-full" style={{ inset: 3 }} />
                <ProtocolLogo slug={p.slug} name={p.protocol} size={Math.round(m * 0.9)} fill ring={false}
                  className={`nc-med relative z-[1] ${isActive ? "nc-med-active" : ""}`} />
              </span>
              <span className={`absolute flex flex-col ${labelPos} pointer-events-none z-[4]`}>
                <span className={`flex items-center gap-0.5 font-bold leading-tight whitespace-nowrap ${route && isActive ? "text-[#00E5FF]" : "text-white"} transition-colors duration-200`}
                  style={{ fontSize: family === "mobile" ? 11 : 12.5, textShadow: "0 1px 8px rgba(0,0,0,.95)" }}>
                  {p.protocol}
                  {route && (
                    <ArrowUpRight aria-hidden
                      className={`h-3 w-3 shrink-0 text-[#00E5FF] transition-opacity duration-200 ${isActive ? "opacity-100" : "opacity-50 md:opacity-0"}`} />
                  )}
                </span>
                <span className="font-mono text-[10px] text-gray-300 whitespace-nowrap" style={{ textShadow: "0 1px 6px rgba(0,0,0,.95)" }}>
                  {fmtBig(p.tvl_usd)}{" "}
                  {d24 != null && (
                    <span className={`font-bold ${d24 >= 0 ? "text-[#22C55E]" : "text-[#EF4444]"}`}>
                      {`${d24 >= 0 ? "+" : "−"}${Math.abs(d24).toFixed(2)}%`}
                    </span>
                  )}
                </span>
                <span className={`font-mono text-[8px] uppercase tracking-wide text-gray-400 whitespace-nowrap h-[12px] transition-opacity duration-200 ${isActive ? "opacity-100" : "opacity-0"}`}
                  style={{ textShadow: "0 1px 6px rgba(0,0,0,.95)" }}>
                  {p.category}{p.dominant_chain ? ` · ${p.dominant_chain}` : ""}
                </span>
              </span>
            </button>
          );
        })}

        {/* film grain */}
        <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none z-[5]" style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,.5) .5px, transparent .6px)",
          backgroundSize: "26px 26px" }} />

        <CoreText env={env} pulse={pulse} stageW={stageW || 900} family={family} />

        {trafficDebug && (
          <pre ref={telemetryElRef} data-testid="nerve-traffic-telemetry"
            className="absolute left-2 top-2 z-20 max-w-[46%] max-h-[64%] overflow-hidden rounded border border-white/10 bg-black/75 p-2 font-mono text-[8.5px] leading-[1.4] text-emerald-300 pointer-events-none whitespace-pre-wrap" />
        )}

        {/* ONE persistent truth chip — bottom-right (§54) */}
        <span data-testid="nerve-legend"
          className="absolute right-2.5 bottom-2 z-10 rounded border border-white/[0.08] bg-black/50 backdrop-blur-[2px] px-2 py-1 font-mono text-[6.5px] sm:text-[7.5px] font-bold uppercase tracking-[0.13em] text-gray-400 pointer-events-none whitespace-nowrap">
          Currents = live classified token trades (Kraken) · not fund flows
        </span>
      </div>
    </div>
  );
};

const StaticFallback = ({ protocols, pulse }: { protocols: any[]; pulse: any }) => (
  <div className="rounded-xl border border-white/[0.06] bg-[#08090c] p-4" data-testid="nerve-static-fallback">
    <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400">
      DeFi network · total TVL {fmtBig(pulse?.tvl_usd)}
    </p>
    <div className="mt-2 divide-y divide-white/[0.04]">
      {(protocols || []).slice(0, 6).map((p) => (
        <div key={p.slug} className="flex items-center gap-3 py-2">
          <ProtocolLogo slug={p.slug} name={p.protocol} size={28} />
          <span className="font-bold text-[13px] text-white flex-1 truncate">{p.protocol}</span>
          <span className="font-mono text-[12px] text-gray-300">{fmtBig(p.tvl_usd)}</span>
          <Pct v={p.tvl_change_24h_pct} className="!text-[11px]" />
        </div>
      ))}
    </div>
  </div>
);

class NerveBoundary extends Component<any, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    if (this.state.failed) return <StaticFallback protocols={this.props.protocols} pulse={this.props.pulse} />;
    return this.props.children;
  }
}

const STALE_MS = 15 * 60 * 1000;

export const DeFiNerveCenter = ({ protocols, pulse, meta }: {
  protocols: any[]; pulse: any; meta?: { updated_at?: string; stale?: boolean } | null;
}) => {
  const rows = protocols || [];
  if (!rows.length) return null;
  const updatedAt = meta?.updated_at ? Date.parse(meta.updated_at) : null;
  const isStale = !!meta?.stale || (updatedAt != null && Date.now() - updatedAt > STALE_MS);
  return (
    <div data-testid="defi-activity-map"
      className="flex flex-col rounded-xl border border-white/[0.08] bg-[#0b0c10] p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#00E5FF] flex items-center gap-2">
            DeFi Nerve Center
            <span data-testid="nerve-freshness"
              className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[7.5px] font-bold tracking-[0.14em] ${
                isStale ? "border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]" : "border-[#34D399]/40 bg-[#34D399]/10 text-[#34D399]"}`}>
              <span aria-hidden className={`h-1 w-1 rounded-full ${isStale ? "bg-[#F59E0B]" : "bg-[#34D399] animate-pulse motion-reduce:animate-none"}`} />
              {isStale ? "STALE" : "LIVE"}
            </span>
            <button type="button" aria-label={DISCLAIMER} title={DISCLAIMER} data-testid="map-info"
              className="text-gray-500 hover:text-gray-300 focus:text-gray-300 outline-none focus-visible:ring-1 focus-visible:ring-[#00E5FF]/60 rounded">
              <Info aria-hidden className="h-3 w-3" />
            </button>
          </p>
          <p className="text-[12px] text-gray-400 mt-1">
            Live protocol activity map — TVL &amp; 24H movement.
            {isStale
              ? updatedAt != null && <span className="text-gray-500"> Updated {timeAgo(meta!.updated_at!)}.</span>
              : <span className="text-gray-500" data-testid="nerve-live-note"> Refreshing continuously.</span>}
          </p>
        </div>
        <Link to="/defi/protocols" data-testid="activity-map-view-all"
          onClick={() => sectorTrack("market_module_interaction", "activity_map_view_all", "defi")}
          className="group font-mono text-[11px] font-bold text-[#34D399] hover:text-white transition-colors whitespace-nowrap">
          All protocols <ArrowRight className="inline h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5" />
        </Link>
      </div>
      <div className={`mt-3 flex-1 ${isStale ? "opacity-80 saturate-[.8]" : ""}`}>
        <NerveBoundary protocols={rows} pulse={pulse}>
          <NerveVisual protocols={rows} pulse={pulse} isStale={isStale} />
        </NerveBoundary>
      </div>
      {/* semantic data equivalent — values never live only in pixels */}
      <table className="sr-only">
        <caption>DeFi protocol network data</caption>
        <tbody>
          {rows.slice(0, 8).map((p) => (
            <tr key={p.slug}>
              <td>{p.protocol}</td><td>{fmtBig(p.tvl_usd)}</td>
              <td>{p.tvl_change_24h_pct != null ? `${p.tvl_change_24h_pct}% 24H` : "n/a"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p data-testid="map-disclaimer" className="font-mono text-[8.5px] uppercase tracking-wider text-gray-500 mt-2.5">
        Connections represent ecosystem relationships and activity context · pulse intensity reflects relative 24H protocol movement · directional currents reflect live classified spot trades for associated protocol tokens (Kraken taker side) · visual intensity is normalized and aggregated for readability · signals do not represent direct fund flows into or out of protocol TVL · DeFiLlama + Kraken
      </p>
    </div>
  );
};
